import { yup } from 'common/validation/initYup';
import { ValidationSchema } from 'common/validation/ValidationSchema';
import { Type, Transform, plainToClass, classToPlain } from 'class-transformer';
import { TypeTransformDecimal } from 'common/dto/shared/types/TypeTransformDecimal';
import { Decimal } from 'decimal.js-light';
import { ApiProperty } from '@nestjs/swagger';
import type { IDto, IDtoPartial } from 'common/dto/shared/DtoInterfaces';
import { GenerateDto } from "cli/generate/decorators/GenerateDecorators";
import { SchemaLabel, SchemaPageNumber, SchemaPageSize } from "cli/generate/decorators/SchemaDecorators";
import { ColumnSortDirections } from 'client/ui/table/TableColumnSort';
import { InvoiceBatchColumnSort } from './types/InvoiceBatchQueryTypes';

@GenerateDto()
class InvoiceBatchQuery {
  @SchemaPageNumber()
  pageNumber!: number;
  @SchemaPageSize()
  pageSize!: number;
  @SchemaLabel("Ordinamento")
  orderBy?: InvoiceBatchColumnSort = InvoiceBatchColumnSort.createdAt;
  @SchemaLabel("Direzione di Ordinamento")
  orderByDirection?: ColumnSortDirections = ColumnSortDirections.descend;

  @SchemaLabel('CF/P.IVA')
  supplierFiscalNumber?: string | null;
  @SchemaLabel('Denominazione')
  supplierName?: string | null;

}

/**
 * Rappresentazione DTO della classe InvoiceBatchQuery 
 * Hash: 338f2933a31b494476c5d56712a04b77
 */
@ValidationSchema(() => InvoiceBatchQuerySchema)
export class InvoiceBatchQueryDto {
  @ApiProperty({ required: false, type: Number, description: 'Numero di pagina' })
  pageNumber!: number;
  @ApiProperty({ required: false, type: Number, description: 'Dimensione pagina' })
  pageSize!: number;
  @ApiProperty({ required: false, enum: ['createdAt', 'id'], description: 'Ordinamento' })
  orderBy?: InvoiceBatchColumnSort | undefined = InvoiceBatchColumnSort.createdAt;
  @ApiProperty({ required: false, enum: ['ASC', 'DESC'], description: 'Direzione di Ordinamento' })
  orderByDirection?: ColumnSortDirections | undefined = ColumnSortDirections.descend;
  @ApiProperty({ required: false, type: String, description: 'CF/P.IVA' })
  supplierFiscalNumber?: string | null | undefined;
  @ApiProperty({ required: false, type: String, description: 'Denominazione' })
  supplierName?: string | null | undefined;

  /** Proprietà che identifica i DTO */
  readonly __dto!: any;

  /**
   * Crea una nuova istanza con i valori forniti
   */
  constructor(values?: IDtoPartial<InvoiceBatchQueryDto>) {
    if (values != null) {
      Object.assign(this, values instanceof InvoiceBatchQueryDto ? values : plainToClass(InvoiceBatchQueryDto, values));
    }
  }

  async validate(options?: any) {
    const validated = await InvoiceBatchQuerySchema.validate(classToPlain(this), options);
    return new InvoiceBatchQueryDto(validated);
  }
}

export const InvoiceBatchQuerySchema = yup
  .object({
    pageNumber: yup.number().default(1).min(1).integer().positive().label('Numero di pagina'),
    pageSize: yup.number().default(20).min(10).max(50).integer().positive().label('Dimensione pagina'),
    orderBy: yup.string().oneOfEnum(InvoiceBatchColumnSort).default(InvoiceBatchColumnSort.createdAt).label('Ordinamento'),
    orderByDirection: yup.string().oneOfEnum(ColumnSortDirections).default(ColumnSortDirections.descend).label('Direzione di Ordinamento'),
    supplierFiscalNumber: yup.string().nullable().label('CF/P.IVA'),
    supplierName: yup.string().nullable().label('Denominazione')
  })
  .noUnknown()
  .meta({ schemaName: "InvoiceBatchQuerySchema" })
  .required();
